import {useMemo, useState} from 'react'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'
import {SquareIcon} from '@/components/ui/icon/Icon'
import {Menu} from '@/components/ui/menu-atoms/MenuAtoms'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {MenuButton, MenuProvider} from '@ariakit/react'
import {
    StyledAnimatedChevron,
    StyledMenuItem,
    StyledStatusTriggerButton
} from '@/features/agenda/components/agenda-event-status-dropdown/style'
import {agendaEventStatusMapToDarkColor, agendaEventStatusMapToLabel} from '@/features/agenda/utils'
import {AgendaEventStatus} from '@/features/agenda/types'
import {Text} from '@/components/ui/text/Text'
import dayjs from 'dayjs'

const EventStatusSchema = AgendaEventFieldsSchema.pick({status: true, isGroupTreatment: true})
type EventStatusSchema = z.infer<typeof EventStatusSchema>

export const AgendaEventStatusDropdown = ({
    eventStatus,
    eventDateTime,
    isDisabled,
    disabledStatuses
}: {
    eventDateTime?: Date
    eventStatus?: AgendaEventStatus
    isDisabled?: boolean
    disabledStatuses?: AgendaEventStatus[]
}) => {
    const {t} = useTranslation()
    const [isOpen, setOpen] = useState(false)
    const {control} = useFormContext<EventStatusSchema>()
    const status = useWatch({control, name: 'status'})
    const isGroupTreatment = useWatch({control, name: 'isGroupTreatment'})
    const groupTreatmentDisabledStatuses = useMemo((): AgendaEventStatus[] => {
        return ['withdrawn', 'missed']
    }, [])

    // if date is in the past, disable missed and completed status
    const isDateInThePast = useMemo(() => {
        return eventDateTime ? dayjs(eventDateTime).isBefore(dayjs()) : false
    }, [eventDateTime])

    const eventStatuses = useMemo(() => {
        return !eventDateTime
            ? ObjectKeys(agendaEventStatusMapToLabel).filter(status => status == 'scheduled' || status == 'provisional')
            : ObjectKeys(agendaEventStatusMapToLabel)
    }, [eventDateTime])

    if (!status && eventStatus) {
        return (
            <StyledStatusTriggerButton variant="secondary" $status={eventStatus} $isDisabled={isDisabled}>
                <SquareIcon className="custom" size={8} color={agendaEventStatusMapToDarkColor[eventStatus]} />
                {t(agendaEventStatusMapToLabel[eventStatus])}
                {!isDisabled && <StyledAnimatedChevron $isOpen={isOpen} />}
            </StyledStatusTriggerButton>
        )
    }

    if (!status) {
        return (
            <Text palette="danger" shade="700" size="textSm">
                {t('agenda:status_not_available')}
            </Text>
        )
    }
    return (
        <Controller
            control={control}
            name="status"
            render={({field}) => (
                <MenuProvider open={isOpen} setOpen={setOpen}>
                    <MenuButton
                        disabled={isDisabled}
                        showOnHover={false}
                        render={
                            <StyledStatusTriggerButton variant="secondary" $status={status}>
                                <SquareIcon
                                    className="custom"
                                    size={8}
                                    color={agendaEventStatusMapToDarkColor[status]}
                                />
                                {t(agendaEventStatusMapToLabel[status])}
                                {!isDisabled && <StyledAnimatedChevron $isOpen={isOpen} />}
                            </StyledStatusTriggerButton>
                        }
                    />
                    <Menu portal={false} onClick={event => event.stopPropagation()}>
                        {eventStatuses
                            .filter(status => status != 'to_be_confirmed' && status != 'tentative')
                            .map(status => (
                                <StyledMenuItem
                                    key={status}
                                    hideOnClick={false}
                                    disabled={
                                        (eventStatus == 'cancelled' && status != 'scheduled') ||
                                        (isGroupTreatment && groupTreatmentDisabledStatuses.includes(status)) ||
                                        (!isDateInThePast && (status == 'missed' || status == 'completed')) ||
                                        disabledStatuses?.includes(status)
                                    }
                                    onClick={e => {
                                        e.stopPropagation()
                                        field.onChange(status, {shouldDirty: true})
                                        setOpen(false)
                                    }}
                                    $isSelected={status == field.value}
                                >
                                    {t(agendaEventStatusMapToLabel[status])}
                                </StyledMenuItem>
                            ))}
                    </Menu>
                </MenuProvider>
            )}
        />
    )
}
